'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { Button, Icon, Popover, PopoverContent, PopoverTrigger, Separator } from '@repo/ui';
import { Close, Drag, Hide, Search, Settings, Show } from '@repo/ui/icons';
import { Reorder, useDragControls } from 'motion/react';
import { type ChangeEvent, type PropsWithChildren, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OptionTableProperties<T> {
  label: string;
  value: T;
  action?: (option: OptionTableProperties<T>) => void;
  isActive: boolean;
  isDisabled: boolean;
}
export interface TablePropertiesPopoverProps<T> {
  disabled?: boolean;
  options: OptionTableProperties<T>[];
  setColumnOrder?: (orderedIds: OptionTableProperties<T>[]) => void;
}

// ---------------------------------------------------------------------------
// useIsCoarsePointer – detects touch devices for disabling drag-and-drop
// ---------------------------------------------------------------------------

function useIsCoarsePointer() {
  const $ = _c(2);
  const [isCoarse, setIsCoarse] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const mql = window.matchMedia("(pointer: coarse)");
      setIsCoarse(mql.matches);
      const handler = e => setIsCoarse(e.matches);
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isCoarse;
}

// ---------------------------------------------------------------------------
// TablePropertiesSearch
// ---------------------------------------------------------------------------

interface TablePropertiesSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}
function TablePropertiesSearch(t0) {
  const $ = _c(17);
  const {
    value,
    onChange,
    placeholder,
    disabled
  } = t0;
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(value || "");
  let t1;
  if ($[0] !== onChange) {
    t1 = t2 => {
      const {
        target: t3
      } = t2;
      const {
        value: value_0
      } = t3;
      setLocalValue(value_0);
      onChange(value_0);
    };
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleInputChange = t1;
  let t2;
  if ($[2] !== onChange) {
    t2 = () => {
      setLocalValue("");
      onChange("");
    };
    $[2] = onChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleReset = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => inputRef.current?.focus();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center"><Icon icon={<Search />} className="text-muted-foreground" /></div>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== disabled || $[7] !== handleInputChange || $[8] !== localValue || $[9] !== placeholder) {
    t5 = <input ref={inputRef} value={localValue} placeholder={placeholder} onChange={handleInputChange} disabled={disabled} className="text-foreground placeholder:text-muted-foreground disabled:text-muted-foreground flex-1 border-none bg-transparent text-xs leading-normal outline-none" />;
    $[6] = disabled;
    $[7] = handleInputChange;
    $[8] = localValue;
    $[9] = placeholder;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== handleReset || $[12] !== localValue.length) {
    t6 = localValue.length > 0 && <Button variant="ghost" size="iconSm" onClick={handleReset} className="right-2xs absolute" aria-label="Clear search"><Close /></Button>;
    $[11] = handleReset;
    $[12] = localValue.length;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <div className="gap-2xs relative flex w-full cursor-text items-center px-[16px] py-[10px]" onClick={t3}>{t4}{t5}{t6}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

// ---------------------------------------------------------------------------
// TablePropertiesItem
// ---------------------------------------------------------------------------

interface TablePropertiesItemProps<T> extends PropsWithChildren {
  item: OptionTableProperties<T>;
}
function TablePropertiesItem(t0) {
  const $ = _c(24);
  const {
    children,
    item
  } = t0;
  let t1;
  if ($[0] !== item) {
    t1 = () => !item.isDisabled && item.action?.(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `table-properties-option-${String(item.value)}`;
  const t3 = item.isDisabled ? "cursor-not-allowed" : "hover:bg-background cursor-pointer";
  let t4;
  if ($[2] !== t3) {
    t4 = cn("px-xs py-2xs group flex items-center justify-between rounded-lg select-none", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = item.isActive ? "text-foreground" : "text-muted-foreground";
  let t6;
  if ($[4] !== t5) {
    t6 = cn("text-xs contents", t5);
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== children || $[7] !== t6) {
    t7 = <span className={t6}>{children}</span>;
    $[6] = children;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== item.isActive) {
    t8 = item.isActive ? <Show /> : <Hide />;
    $[9] = item.isActive;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = item.isDisabled ? "text-muted-foreground" : item.isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground";
  let t10;
  if ($[11] !== t9) {
    t10 = cn(t9);
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t8) {
    t11 = <Icon icon={t8} className={t10} />;
    $[13] = t10;
    $[14] = t8;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t4 || $[18] !== t7) {
    t12 = <div className={t4}>{t7}{t11}</div>;
    $[16] = t11;
    $[17] = t4;
    $[18] = t7;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t1 || $[21] !== t12 || $[22] !== t2) {
    t13 = <div onClick={t1} data-testid={t2}>{t12}</div>;
    $[20] = t1;
    $[21] = t12;
    $[22] = t2;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
}

// ---------------------------------------------------------------------------
// TablePropertiesDraggableItem
// ---------------------------------------------------------------------------

function TablePropertiesDraggableItem(t0) {
  const $ = _c(37);
  const {
    children,
    item
  } = t0;
  const controls = useDragControls();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      position: "relative"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== item) {
    t2 = () => !item.isDisabled && item.action?.(item);
    $[1] = item;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `table-properties-option-${String(item.value)}`;
  const t4 = item.isDisabled ? "cursor-not-allowed" : "hover:bg-background cursor-pointer";
  let t5;
  if ($[3] !== t4) {
    t5 = cn("px-xs py-2xs group flex items-center justify-between rounded-lg select-none", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== controls) {
    t6 = e => controls.start(e);
    $[5] = controls;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon icon={<Drag />} className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing" />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6) {
    t8 = <div onPointerDown={t6} onClick={_temp}>{t7}</div>;
    $[8] = t6;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = item.isActive ? "text-foreground" : "text-muted-foreground";
  let t10;
  if ($[10] !== t9) {
    t10 = cn("text-xs", t9);
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== children || $[13] !== t10) {
    t11 = <span className={t10}>{children}</span>;
    $[12] = children;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t8) {
    t12 = <div className="gap-2xs flex items-center">{t8}{t11}</div>;
    $[15] = t11;
    $[16] = t8;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== item.isActive) {
    t13 = item.isActive ? <Show /> : <Hide />;
    $[18] = item.isActive;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = item.isDisabled ? "text-muted-foreground" : item.isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground";
  let t15;
  if ($[20] !== t14) {
    t15 = cn(t14);
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t13 || $[23] !== t15) {
    t16 = <Icon icon={t13} className={t15} />;
    $[22] = t13;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t12 || $[26] !== t16 || $[27] !== t5) {
    t17 = <div className={t5}>{t12}{t16}</div>;
    $[25] = t12;
    $[26] = t16;
    $[27] = t5;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t17 || $[30] !== t2 || $[31] !== t3) {
    t18 = <div onClick={t2} data-testid={t3}>{t17}</div>;
    $[29] = t17;
    $[30] = t2;
    $[31] = t3;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== controls || $[34] !== item || $[35] !== t18) {
    t19 = <Reorder.Item value={item} dragListener={false} dragControls={controls} style={t1}>{t18}</Reorder.Item>;
    $[33] = controls;
    $[34] = item;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
}

// ---------------------------------------------------------------------------
// TablePropertiesPopover
// ---------------------------------------------------------------------------
function _temp(e_0) {
  e_0.preventDefault();
  e_0.stopPropagation();
}
function TablePropertiesPopoverInner(t0) {
  const $ = _c(35);
  const {
    options,
    disabled,
    setColumnOrder
  } = t0;
  const isCoarse = useIsCoarsePointer();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  bb0: {
    if (!search) {
      t1 = options;
      break bb0;
    }
    let t2;
    if ($[0] !== options || $[1] !== search) {
      let t3;
      if ($[3] !== search) {
        t3 = option => {
          if (!option.label) {
            return false;
          }
          return option.label.toLowerCase().includes(search.toLowerCase());
        };
        $[3] = search;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      t2 = options.filter(t3);
      $[0] = options;
      $[1] = search;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const filteredOptions = t1;
  let t2;
  if ($[5] !== options || $[6] !== setColumnOrder) {
    t2 = reordered => {
      const reorderedValues = new Set(reordered.map(_temp2));
      const nextReordered = reordered.values();
      setColumnOrder?.(options.map(option_1 => reorderedValues.has(option_1.value) ? nextReordered.next().value ?? option_1 : option_1));
    };
    $[5] = options;
    $[6] = setColumnOrder;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleReorder = t2;
  const t3 = isOpen && "rotate-180";
  let t4;
  if ($[8] !== t3) {
    t4 = cn("text-secondary-foreground transition-transform duration-300", t3);
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <Settings className={t4} />;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== disabled || $[13] !== t5) {
    t6 = <PopoverTrigger asChild={true}><Button variant="ghost" size="iconSm" disabled={disabled} aria-label="Table properties" data-testid="table-properties-trigger">{t5}</Button></PopoverTrigger>;
    $[12] = disabled;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== search) {
    t7 = <TablePropertiesSearch value={search} onChange={setSearch} placeholder="Search" />;
    $[15] = search;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Separator />;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== filteredOptions || $[19] !== handleReorder || $[20] !== isCoarse || $[21] !== setColumnOrder) {
    t9 = filteredOptions.length > 0 && (setColumnOrder && !isCoarse ? <Reorder.Group axis="y" values={filteredOptions} onReorder={handleReorder}>{filteredOptions.map(_temp3)}</Reorder.Group> : filteredOptions.map(_temp4));
    $[18] = filteredOptions;
    $[19] = handleReorder;
    $[20] = isCoarse;
    $[21] = setColumnOrder;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== filteredOptions.length) {
    t10 = filteredOptions.length === 0 && <p className="text-muted-foreground p-2xs flex w-full justify-center text-xs">No table properties found</p>;
    $[23] = filteredOptions.length;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = <div className="p-xs overflow-y-auto">{t9}{t10}</div>;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== t7) {
    t12 = <PopoverContent side="bottom" className="flex max-h-[450px] w-[220px] flex-col rounded-lg p-0">{t7}{t8}{t11}</PopoverContent>;
    $[28] = t11;
    $[29] = t7;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== isOpen || $[32] !== t12 || $[33] !== t6) {
    t13 = <Popover open={isOpen} onOpenChange={setIsOpen}>{t6}{t12}</Popover>;
    $[31] = isOpen;
    $[32] = t12;
    $[33] = t6;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}

// oxlint-disable-next-line typescript/consistent-type-assertions
function _temp4(item_0) {
  return <TablePropertiesItem key={String(item_0.value)} item={item_0}>{item_0.label}</TablePropertiesItem>;
}
function _temp3(item) {
  return <TablePropertiesDraggableItem key={String(item.value)} item={item}>{item.label}</TablePropertiesDraggableItem>;
}
function _temp2(option_0) {
  return option_0.value;
}
export const TablePropertiesPopover = TablePropertiesPopoverInner as <T>(props: TablePropertiesPopoverProps<T>) => ReturnType<typeof TablePropertiesPopoverInner>;